import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Leaf, Sun, Sprout, ExternalLink, Info } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { TenantShell, BrandButton } from "../../components/tenant/TenantShell";
import { mainSiteUrl, setSectorFit } from "../../lib/tenant";

const SECTOR_ICONS = { climate: Leaf, nature: Sprout, energy: Sun };

export default function TenantSectorGate() {
  const { sub, config, to } = useTenant();
  const navigate = useNavigate();
  const [answer, setAnswer] = useState(null);
  const sectors = config.focus_sectors || [];
  const question = config.sector_gate_question || `Is your business operating in ${config.focus_sectors_text}?`;

  const yes = () => { setSectorFit(sub, true); navigate(to("/start")); };
  const no = () => { setSectorFit(sub, false); setAnswer("no"); };

  return (
    <TenantShell>
      <div className="mx-auto max-w-xl" data-testid="tenant-sector-gate">
        <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--tenant)" }}>{config.name} · Before we begin</p>
        {answer !== "no" ? (
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
            <h1 className="text-2xl font-extrabold leading-tight tracking-tight sm:text-3xl" data-testid="sector-gate-question">{question}</h1>
            {sectors.length > 0 && (
              <ul className="mt-4 flex flex-wrap gap-2" data-testid="sector-gate-sectors">
                {sectors.map((s) => { const Icon = SECTOR_ICONS[s.toLowerCase()] || Leaf; return (
                  <li key={s} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold capitalize" style={{ borderColor: "var(--tenant)", color: "var(--tenant)" }}><Icon size={12} /> {s}</li>
                ); })}
              </ul>
            )}
            <p className="mt-4 text-sm text-neutral-600">{config.name} supports businesses in these focus areas. Tell us which applies to you so we can route you to the right place.</p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <BrandButton onClick={yes} className="flex-1" data-testid="sector-gate-yes">Yes — my business operates here <ArrowRight size={16} /></BrandButton>
              <button type="button" onClick={no} data-testid="sector-gate-no"
                className="flex-1 rounded-full border border-neutral-300 bg-white px-6 py-3 text-sm font-bold text-neutral-700 transition-colors hover:bg-neutral-50">
                No — it doesn&apos;t
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="sector-gate-no-fit">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-neutral-100 text-neutral-500"><Info size={20} /></span>
            <h1 className="mt-4 text-2xl font-extrabold leading-tight tracking-tight">Thanks for your interest</h1>
            <p className="mt-3 text-sm leading-relaxed text-neutral-600" data-testid="sector-gate-no-fit-message">
              Your business doesn&apos;t fit {config.name}&apos;s current focus areas, which are <b className="text-neutral-800">{config.focus_sectors_text}</b>.
              That doesn&apos;t mean there isn&apos;t funding for you — Clinq works with a wider network of funders across every sector.
            </p>
            <a href={mainSiteUrl("/get-started?fresh=1")} data-testid="sector-gate-other-funders-link"
              className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white shadow-sm transition-transform hover:-translate-y-0.5"
              style={{ background: "var(--tenant)" }}>
              Find out who else can fund you <ExternalLink size={14} />
            </a>
            <button type="button" onClick={() => setAnswer(null)} className="mt-4 block text-xs font-semibold text-neutral-500 underline" data-testid="sector-gate-change-answer">Change my answer</button>
          </div>
        )}
      </div>
    </TenantShell>
  );
}
